import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';
import { AccountType } from '@/types/account';

interface AccountContextType {
  accountType: AccountType | null;
  setAccountType: (type: AccountType) => void;
  isLoading: boolean;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const AccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;

    const loadAccountType = async () => {
      if (!user) {
        if (mounted) {
          setIsLoading(false);
        }
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('account_type')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error loading account type:', error);
          if (mounted) {
            setIsLoading(false);
          }
          return;
        }

        if (mounted) {
          if (profile?.account_type) {
            setAccountType(profile.account_type as AccountType);
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error in loadAccountType:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadAccountType();

    return () => {
      mounted = false;
    };
  }, [user]);

  const handleSetAccountType = async (type: AccountType) => {
    if (!user) return;

    try {
      // First check if profile exists
      const { data: existingProfile, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking profile:', checkError);
        throw checkError;
      }

      let result;
      if (existingProfile) {
        // Update existing profile
        result = await supabase
          .from('profiles')
          .update({ 
            account_type: type,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
      } else {
        // Create new profile
        result = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            account_type: type,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
      }

      if (result.error) {
        console.error('Error setting account type:', result.error);
        throw result.error;
      }

      setAccountType(type);
    } catch (error) {
      console.error('Error in handleSetAccountType:', error);
      throw error;
    }
  };

  return (
    <AccountContext.Provider value={{ accountType, setAccountType: handleSetAccountType, isLoading }}>
      {children}
    </AccountContext.Provider>
  );
};

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
}; 